import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useIntl } from 'platform-components';

import {
    useTeacherReserveActivities,
    useTeacherReserveAwards,
    useTeacherReserveParticipant,
    getTeacherReserveProgressPoints,
    usePostTeacherReserveMentor,
} from 'store/teacher-reserve';

import { TEACHER_RESERVE_NAMESPACE } from '@edu-frontend/modules/src/features/teacherReserve';
import { TeacherReserveWidgetProps } from '@edu-frontend/modules/src/widgets/teacherReserveCarousels';
import { useBunkerOnboarding, TEACHER_RESERVE_LEADERBOARD_ONBOARDING_KEY } from 'hooks/useBunkerOnboarding';
import { useActivityCardButtonHandler } from './use-activity-card-button-handler';
import { useAwardCardHandlers } from './use-award-card-handlers';

/** Отдает общие параметры для виджета и страницы Кадрового резерва */
export const useTeacherReserveParams = (): TeacherReserveWidgetProps & { error: boolean } => {
    const intl = useIntl();

    const {
        data: activities = [],
        isLoading: activitiesLoading,
        error: activitiesError,
    } = useTeacherReserveActivities();
    const { data: awards = [], isLoading: awardsLoading, error: awardsError } = useTeacherReserveAwards();
    const {
        data: participant,
        isLoading: participantLoading,
        error: participantError,
    } = useTeacherReserveParticipant();

    const { openOnboarding } = useBunkerOnboarding({ id: TEACHER_RESERVE_NAMESPACE });
    const { openOnboarding: openLeaderBoardOnboarding } = useBunkerOnboarding({
        id: TEACHER_RESERVE_LEADERBOARD_ONBOARDING_KEY,
    });

    const progressPoints = useSelector(getTeacherReserveProgressPoints);

    const loading = activitiesLoading || awardsLoading || participantLoading;

    const getIntlText = useCallback(
        (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
        [intl]
    );

    const { handleActivityCardButtonClick, handleInviteReferralClick } = useActivityCardButtonHandler({
        referrerLinkId: participant?.referrerLinkId,
    });
    const awardCardHandlers = useAwardCardHandlers();

    const onInfoClick = useCallback(() => {
        openOnboarding({
            slideId: intl.formatMessage({ id: 'teacherReserve.pointsPlate.onboardingSlideId' }),
        });
    }, [openOnboarding, intl]);

    const [onMentorAccept] = usePostTeacherReserveMentor();

    return {
        onInfoClick,
        onRatingInfoClick: openLeaderBoardOnboarding,
        getIntlText: getIntlText,
        loading: loading,
        onActivityCardButtonClick: handleActivityCardButtonClick,
        participant: participant,
        progressPoints: progressPoints,
        sortedActivities: activities,
        sortedAwards: awards,
        onInviteReferralClick: handleInviteReferralClick,
        ...awardCardHandlers,
        error: !!(participantError || awardsError || activitiesError),
        onMentorAccept,
    };
};
